import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

// Lucide icon imports
import {
  FileText,
  AlertCircle,
  Users,
  Tag,
  Bell,
  Settings,
  Check,
  Download,
} from 'lucide-mui';

import moment from 'moment';
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../reducers';
import {
  markNotificationAsReadWithHistory,
  clearAllNotificationsWithHistory,
} from '../dashboardSlice';
import NotificationsEmptyState from './NotificationsEmptyState';
import ActionDialog from '../../globalModals/ActionDialog';
import { getFile } from '../../../services/fileSystemService';
import defaultThumbnail from '../../../assets/recording/DefaultThumbnail.svg';
import { FileType } from '../../../types';

interface NotificationsProps {
  open: boolean;
  onClose: () => void;
}

interface NotificationFile {
  fileId: string;
  file: FileType | null;
  isLoading: boolean;
}

const Notifications: React.FC<NotificationsProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dashboard } = useSelector((state: RootState) => state);
  const { notifications } = dashboard;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [notificationFiles, setNotificationFiles] = useState<
    NotificationFile[]
  >([]);

  // Fetch file data for a file notification (similar to library approach)
  const fetchFileData = useCallback(
    async (fileId: string) => {
      // Check if we already have this file data
      const existingFile = notificationFiles.find((f) => f.fileId === fileId);
      if (existingFile) return;

      // Add loading placeholder
      setNotificationFiles((prev) => [
        ...prev,
        { fileId, file: null, isLoading: true },
      ]);

      try {
        // Fetch the complete file data (same as library does)
        const fileData = await getFile(fileId);

        setNotificationFiles((prev) =>
          prev.map((f) =>
            f.fileId === fileId
              ? { ...f, file: fileData.file, isLoading: false }
              : f
          )
        );
      } catch (error) {
        console.error('Error fetching notification file data:', error);
        setNotificationFiles((prev) =>
          prev.map((f) =>
            f.fileId === fileId ? { ...f, file: null, isLoading: false } : f
          )
        );
      }
    },
    [notificationFiles]
  );

  // Fetch file data for file-related notifications
  useEffect(() => {
    const fileNotifications = notifications.filter((n) =>
      ['transcription', 'processing', 'import'].includes(n.type)
    );

    fileNotifications.forEach((notification) => {
      fetchFileData(notification.id);
    });
  }, [notifications, fetchFileData]);

  useEffect(() => {
    const currentTitle = document.title;
    const baseTitle = currentTitle.replace(/^\(\d+\)\s/, '');
    const unreadCount = notifications.filter((n) => !n.read).length;

    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [notifications]);

  const handleNotificationClick = (id: string, link?: string) => {
    if (link) {
      window.location.href = link;
    }
    dispatch(markNotificationAsReadWithHistory(id));
    setSnackbarMessage(t('drawer.notifications.markedAsRead'));
    setSnackbarOpen(true);
  };

  const handleDismissAll = () => {
    dispatch(clearAllNotificationsWithHistory());
    setSnackbarMessage(t('drawer.notifications.allMarkedAsRead'));
    setSnackbarOpen(true);
    onClose();
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'transcription':
        return <FileText style={{ width: 16, height: 16 }} />;
      case 'tag':
        return <Tag style={{ width: 16, height: 16 }} />;
      case 'team':
        return <Users style={{ width: 16, height: 16 }} />;
      case 'error':
        return <AlertCircle style={{ width: 16, height: 16 }} />;
      case 'processing':
        return <Settings style={{ width: 16, height: 16 }} />;
      case 'import':
        return <Download style={{ width: 16, height: 16 }} />;
      default:
        return <Bell style={{ width: 16, height: 16 }} />;
    }
  };

  const getIconBackground = (type: string) => {
    switch (type) {
      case 'error':
        return `${theme.palette.error.main}15`;
      case 'transcription':
        return `${theme.palette.primary.main}15`;
      case 'team':
        return `${theme.palette.success.main}15`;
      case 'tag':
        return `${theme.palette.warning.main}15`;
      case 'processing':
        return `${theme.palette.info.main}15`;
      case 'import':
        return `${theme.palette.secondary.main}15`;
      default:
        return `${theme.palette.primary.main}15`;
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'error':
        return theme.palette.error.main;
      case 'transcription':
        return theme.palette.primary.main;
      case 'team':
        return theme.palette.success.main;
      case 'tag':
        return theme.palette.warning.main;
      case 'processing':
        return theme.palette.info.main;
      case 'import':
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const hasAnyUnreadNotifications = notifications.some((n) => !n.read);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Sort notifications by timestamp in descending order (newest first)
  const sortedNotifications = [...notifications].sort(
    (a, b) => b.timestamp - a.timestamp
  );

  const dialogTitle =
    unreadCount > 0
      ? t('drawer.notifications.titleWithCount', { count: unreadCount })
      : t('drawer.notifications.title');

  // Dynamic button text based on unread count
  const markAsReadButtonText =
    unreadCount === 1
      ? t('drawer.notifications.markAsRead')
      : t('drawer.notifications.markAllAsRead');

  const getFileDataForNotification = (fileId: string) => {
    return notificationFiles.find((f) => f.fileId === fileId);
  };

  const renderNotificationAvatar = (id: string, type: string) => {
    const isFileNotification = [
      'transcription',
      'processing',
      'import',
    ].includes(type);

    if (isFileNotification) {
      const fileData = getFileDataForNotification(id);

      let avatarContent;

      if (fileData?.isLoading) {
        avatarContent = <CircularProgress size={16} />;
      } else if (fileData?.file?.thumbUrl) {
        avatarContent = (
          <img
            src={fileData.file.thumbUrl}
            alt={t('drawer.notifications.altText.fileThumbnail')}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter:
                theme.palette.mode === 'dark' &&
                fileData.file.thumbUrl === defaultThumbnail
                  ? 'invert(1)'
                  : 'none',
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src !== defaultThumbnail) {
                target.src = defaultThumbnail;
              }
            }}
          />
        );
      } else {
        const thumbnailSrc = fileData?.file ? defaultThumbnail : null;

        if (thumbnailSrc) {
          avatarContent = (
            <img
              src={thumbnailSrc}
              alt={t('drawer.notifications.altText.defaultThumbnail')}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
              }}
            />
          );
        } else {
          avatarContent = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: getIconBackground(type),
                color: getIconColor(type),
              }}
            >
              {getIcon(type)}
            </Box>
          );
        }
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '6px',
            overflow: 'hidden',
            flexShrink: 0,
            mt: 0.25,
            backgroundColor: theme.palette.background.default,
          }}
        >
          {avatarContent}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: getIconBackground(type),
          color: getIconColor(type),
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {getIcon(type)}
      </Box>
    );
  };

  return (
    <ActionDialog
      title={dialogTitle}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      actions={
        hasAnyUnreadNotifications
          ? [
              {
                key: 'mark-all-read',
                label: markAsReadButtonText,
                onClick: handleDismissAll,
                startIcon: <Check style={{ width: 16, height: 16 }} />,
                color: 'primary',
                variant: 'contained',
              },
            ]
          : []
      }
      sx={{
        '& .MuiDialogActions-root': {
          p: '8px 16px 16px',
        },
        '& .MuiDialogContent-root': {
          pb: 1,
        },
      }}
    >
      <Box sx={{ m: 0, p: 0 }}>
        {notifications.length > 0 ? (
          <List
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              py: 0,
              px: 0,
              m: 0,
            }}
          >
            {sortedNotifications.map(
              ({ id, type, title, message, timestamp, read, link }, index) => (
                <React.Fragment key={id}>
                  <ListItem
                    alignItems="flex-start"
                    onClick={() => handleNotificationClick(id, link)}
                    disablePadding
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      py: 1.5,
                      px: 2,
                      opacity: read ? 0.7 : 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        opacity: 1,
                        '& .dismiss-button': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        width: '100%',
                      }}
                    >
                      {renderNotificationAvatar(id, type)}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={!read ? 600 : 500}
                            sx={{ color: 'text.primary' }}
                          >
                            {title}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              ml: 1,
                              height: '100%',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.7,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {moment(timestamp).calendar(null, {
                                sameDay: 'h:mm A',
                                lastDay: 'Yesterday',
                                lastWeek: 'ddd',
                                sameElse: 'MMM D',
                              })}
                            </Typography>

                            <IconButton
                              className="dismiss-button"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(markNotificationAsReadWithHistory(id));
                                setSnackbarMessage(
                                  t('drawer.notifications.markedAsRead')
                                );
                                setSnackbarOpen(true);
                              }}
                              sx={{
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                color: 'text.secondary',
                                padding: '2px',
                                ml: 0.5,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              <Check style={{ width: 12, height: 12 }} />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            mt: 0.25,
                          }}
                        >
                          {message}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < sortedNotifications.length - 1 && (
                    <Divider
                      variant="fullWidth"
                      component="li"
                      sx={{ my: 0 }}
                    />
                  )}
                </React.Fragment>
              )
            )}
          </List>
        ) : (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <NotificationsEmptyState />
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ActionDialog>
  );
};

export default Notifications;
